/**
 * Layout schema — the on-disk shape of every event's viewer page.
 *
 * Linked events (events.layout = null) render from a template's
 * `defaultLayout`. Forked events (events.layout populated) render
 * directly from that JSON.
 *
 * The schema is intentionally simple: a `shell` chooses the structural
 * frame, and each shell exposes named `slots` that accept an ordered
 * list of `PrimitiveInstance` entries. The Director mutates this JSON.
 *
 * The Zod validator lives in ./schema.ts — these are the inferred types.
 */

/** Names of all shells the renderer can dispatch to. */
export const SHELL_NAMES = [
  'le_vanilla',
  'le_vanilla_map',
  // Future: 'ultra_running' — keep the union open for now
] as const

export type ShellName = (typeof SHELL_NAMES)[number]

/**
 * A primitive instance in a slot.
 *
 * - `id` is stable per instance and used by the Director for addressing
 *   (selection, drag-targets, inspector binding). Generate as UUID on
 *   insert; persist through serialisation.
 * - `primitive` is the component name looked up in PRIMITIVE_REGISTRY.
 *   Unknown names render an `<UnknownPrimitive>` placeholder rather than
 *   crashing the page — keeps partial registries safe.
 * - `config` is an opaque record of per-instance overrides. Each primitive
 *   declares its own config shape via a Zod schema; the LayoutSchema
 *   accepts any record here and primitives validate their own slice.
 */
export interface PrimitiveInstance {
  /** Stable instance ID. UUID. */
  id: string
  /** Primitive component name. Looked up in PRIMITIVE_REGISTRY. */
  primitive: string
  /** Per-instance config. Primitive-specific shape. */
  config: Record<string, unknown>
}

/**
 * The top-level layout document.
 *
 * `version` is bumped any time the shape itself changes incompatibly.
 * A renderer encountering a future version should fall back to the
 * template's defaultLayout rather than guessing.
 */
export interface Layout {
  version: 1
  shell: ShellName
  slots: Record<string, PrimitiveInstance[]>
}
